import React from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  Link,
  IconButton,
  Divider,
  Stack,
  Tooltip,
} from "@mui/material";
import {
  LinkedIn,
  Instagram,
  LocationOn,
  Email,
  Phone,
  AccessTime,
  Launch,
  CheckCircleOutline,
  Description,
  Code,
  Psychology,
  RecordVoiceOver,
  BarChart,
  Compare,
  Assignment,
  Speed,
  Groups,
  Dns,
  AutoAwesome,
  RocketLaunch,
  School,
  WorkOutline,
  Business,
  SettingsInputComponent,
  InfoOutlined,
  HelpOutline,
  QuestionAnswer,
  Article,
  LiveTv,
  Policy,
  Gavel,
  Security,
  AccountTree,
  VerifiedUser,
  SupportAgent,
  CheckCircle,
  QueryStats,
  PieChart,
  TrendingUp,
} from "@mui/icons-material";
import XIcon from "@mui/icons-material/X";
import { Link as RouterLink } from "react-router-dom";

const FooterLink = ({ to, children, icon: Icon, disabled }) => {
  if (disabled) {
    return (
      <Box
        sx={{
          color: "#CBD5E1",
          fontSize: "0.85rem",
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          py: 0.5,
          cursor: "default",
          userSelect: "none"
        }}
      >
        {Icon && <Icon sx={{ fontSize: 16, color: "#60A5FA" }} />}
        {children}
      </Box>
    );
  }
  return (
    <Link
      component={RouterLink}
      to={to}
      sx={{
        color: "#CBD5E1",
        textDecoration: "none",
        fontSize: "0.85rem",
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        transition: "all 0.2s",
        "&:hover": {
          color: "#60A5FA",
          transform: "translateX(4px)",
        },
      }}
    >
      {Icon && <Icon sx={{ fontSize: 16, color: "#60A5FA" }} />}
      {children}
    </Link>
  );
};

const FeatureItem = ({ icon: Icon, text }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
    <Icon sx={{ fontSize: 18, color: "#60A5FA" }} />
    <Typography variant="body2" sx={{ color: "#CBD5E1", fontSize: "0.85rem" }}>
      {text}
    </Typography>
  </Box>
);

function Footer() {
  const socialIcons = [
    { Icon: XIcon, link: "https://x.com/EvolveX2025" },
    { Icon: LinkedIn, link: "https://www.linkedin.com/in/bindhu-sri-vankadaru-7162053a5/" },
    { Icon: Instagram, link: "https://www.instagram.com/evolvexinnovations?igsh=NGtwd2E3ZmZyZ2Jh" },
  ];

  const platformLinks = [
    { label: "AI Resume Screening", to: "/platform/resume-screening", icon: Description, disabled: true },
    { label: "AI Coding Assessments", to: "/platform/coding-assessments", icon: Code, disabled: true },
    { label: "AI Technical Interviews", to: "/platform/technical-interviews", icon: Psychology, disabled: true },
    { label: "AI HR Interviews", to: "/platform/hr-interviews", icon: RecordVoiceOver, disabled: true },
    { label: "Candidate Ranking", to: "/platform/candidate-ranking", icon: BarChart, disabled: true },
    { label: "AI Scoring Engine", to: "/platform/scoring-engine", icon: AutoAwesome, disabled: true },
    { label: "Candidate Comparison", to: "/platform/comparison", icon: Compare, disabled: true },
    { label: "Interview Reports", to: "/platform/reports", icon: Assignment, disabled: true },
    { label: "Hiring Analytics Dashboard", to: "/platform/analytics", icon: TrendingUp, disabled: true },
  ];

  const solutionsLinks = [
    { label: "Startup Hiring", to: "/solutions/startup", icon: RocketLaunch, disabled: true },
    { label: "Campus Recruitment", to: "/solutions/campus", icon: School, disabled: true },
    { label: "Bulk Hiring", to: "/solutions/bulk", icon: Groups, disabled: true },
    { label: "Technical Hiring", to: "/solutions/technical", icon: Code, disabled: true },
    { label: "Remote Hiring", to: "/solutions/remote", icon: Business, disabled: true },
    { label: "Enterprise Recruitment", to: "/solutions/enterprise", icon: SettingsInputComponent, disabled: true },
  ];

  const resourcesLinks = [
    { label: "How It Works", to: "/how-it-works", icon: HelpOutline, disabled: true },
    { label: "Pricing", to: "/pricing", icon: TrendingUp, disabled: false },
    { label: "Help Center", to: "/help", icon: InfoOutlined, disabled: true },
    { label: "FAQ", to: "/faq", icon: QuestionAnswer, disabled: true },
    { label: "Blog", to: "/blog", icon: Article, disabled: true },
    { label: "Success Stories", to: "/success-stories", icon: VerifiedUser, disabled: true },
    { label: "Webinars", to: "/webinars", icon: LiveTv, disabled: true },
    { label: "API Documentation", to: "/api-docs", icon: Code, disabled: true },
  ];

  const companyLinks = [
    { label: "About Us", to: "/about", icon: InfoOutlined, disabled: false },
    { label: "Contact Us", to: "/contact", icon: Email, disabled: false },
    { label: "Careers", to: "/careers", icon: WorkOutline, disabled: true },
    { label: "Privacy Policy", to: "/privacy", icon: Policy, disabled: true },
    { label: "Terms of Service", to: "/terms", icon: Gavel, disabled: true },
    { label: "Security", to: "/security", icon: Security, disabled: true },
    { label: "Sitemap", to: "/sitemap", icon: AccountTree, disabled: true },
  ];

  return (
    <Box
      component="footer"
      sx={{
        mt: "auto",
        width: "100%",
        bgcolor: "#020617",
        color: "white",
        borderTop: "1px solid rgba(255,255,255,0.05)",
      }}
    >
      <Container maxWidth="xl" sx={{ pt: 10, pb: 6 }}>
        <Grid container spacing={4}>
          {/* Brand Section */}
          <Grid item xs={12} lg={3}>
            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
                <img src="/logo.png" alt="ExiHire Logo" style={{ height: 45 }} />
              </Box>
              <Typography
                variant="body2"
                sx={{
                  lineHeight: 1.8,
                  color: "#94A3B8",
                  mb: 4,
                  fontSize: "0.9rem",
                }}
              >
                The premium AI hiring infrastructure platform. Streamlining your hiring process with automated coding tests, technical interviews, HR interviews, and intelligent candidate insights.
              </Typography>

              <Box sx={{ mb: 4 }}>
                <FeatureItem icon={Speed} text="AI-Powered Evaluations" />
                <FeatureItem icon={RocketLaunch} text="Faster Hiring Process" />
                <FeatureItem icon={QueryStats} text="Data-Driven Decisions" />
                <FeatureItem icon={Groups} text="Scalable for Every Team" />
              </Box>

              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 700, color: "white" }}>
                Follow Us
              </Typography>
              <Box sx={{ display: "flex", gap: 1.5 }}>
                {socialIcons.map(({ Icon, link }, i) => (
                  <IconButton
                    key={i}
                    size="small"
                    component="a"
                    href={link}
                    target="_blank"
                    sx={{
                      color: "#94A3B8",
                      border: "1px solid rgba(255,255,255,0.1)",
                      transition: "all 0.3s",
                      "&:hover": {
                        color: "#60A5FA",
                        borderColor: "#60A5FA",
                        bgcolor: "rgba(96,165,250,0.05)",
                        transform: "translateY(-3px)",
                      },
                    }}
                  >
                    <Icon sx={{ fontSize: 18 }} />
                  </IconButton>
                ))}
              </Box>
            </Box>
          </Grid>

          <Grid item xs={6} sm={4} md={2.25} lg={1.6}>
            <Typography variant="subtitle1" sx={{ mb: 4, fontWeight: 700, fontSize: "1rem", letterSpacing: "0.05em" }}>
              Platform
            </Typography>
            <Stack spacing={2}>
              {platformLinks.map((link) => (
                <FooterLink key={link.label} to={link.to} icon={link.icon} disabled={link.disabled}>
                  {link.label}
                </FooterLink>
              ))}
            </Stack>
          </Grid>

          <Grid item xs={6} sm={4} md={2.25} lg={1.6}>
            <Typography variant="subtitle1" sx={{ mb: 4, fontWeight: 700, fontSize: "1rem", letterSpacing: "0.05em" }}>
              Solutions
            </Typography>
            <Stack spacing={2}>
              {solutionsLinks.map((link) => (
                <FooterLink key={link.label} to={link.to} icon={link.icon} disabled={link.disabled}>
                  {link.label}
                </FooterLink>
              ))}
            </Stack>
          </Grid>

          <Grid item xs={6} sm={4} md={2.25} lg={1.6}>
            <Typography variant="subtitle1" sx={{ mb: 4, fontWeight: 700, fontSize: "1rem", letterSpacing: "0.05em" }}>
              Resources
            </Typography>
            <Stack spacing={2}>
              {resourcesLinks.map((link) => (
                <FooterLink key={link.label} to={link.to} icon={link.icon} disabled={link.disabled}>
                  {link.label}
                </FooterLink>
              ))}
            </Stack>
          </Grid>

          <Grid item xs={6} sm={4} md={2.25} lg={1.6}>
            <Typography variant="subtitle1" sx={{ mb: 4, fontWeight: 700, fontSize: "1rem", letterSpacing: "0.05em" }}>
              Company
            </Typography>
            <Stack spacing={2}>
              {companyLinks.map((link) => (
                <FooterLink key={link.label} to={link.to} icon={link.icon} disabled={link.disabled}>
                  {link.label}
                </FooterLink>
              ))}
            </Stack>
          </Grid>

          {/* Contact Section */}
          <Grid item xs={12} sm={6} md={3} lg={2.2} sx={{ ml: { lg: -4 } }}>
            <Typography variant="subtitle1" sx={{ mb: 4, fontWeight: 700, fontSize: "1rem", letterSpacing: "0.05em" }}>
              Contact Us
            </Typography>
            <Stack spacing={3}>
              <Box sx={{ display: "flex", gap: 2 }}>
                <LocationOn sx={{ color: "#60A5FA", fontSize: 20, mt: 0.5, flexShrink: 0 }} />
                <Typography variant="body2" sx={{ color: "#94A3B8", lineHeight: 1.6, fontSize: "0.85rem" }}>
                  Bhoomatha Thalluri Heights, Kankipadu,<br />
                  Vijayawada-521151
                </Typography>
              </Box>
              <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                <Email sx={{ color: "#60A5FA", fontSize: 20, flexShrink: 0 }} />
                <Typography variant="body2" sx={{ color: "#94A3B8", fontSize: "0.85rem", wordBreak: "break-all" }}>
                  connect@evolvexinnovations.org
                </Typography>
              </Box>
              <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                <Phone sx={{ color: "#60A5FA", fontSize: 20, flexShrink: 0 }} />
                <Typography variant="body2" sx={{ color: "#94A3B8", fontSize: "0.85rem" }}>
                  +91 62815 97586
                </Typography>
              </Box>
              <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                <AccessTime sx={{ color: "#60A5FA", fontSize: 20, flexShrink: 0 }} />
                <Typography variant="body2" sx={{ color: "#94A3B8", fontSize: "0.85rem" }}>
                  24/7 Support Available
                </Typography>
              </Box>
              
              <Box
                sx={{
                  mt: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  bgcolor: "rgba(96,165,250,0.05)",
                  border: "1px solid rgba(96,165,250,0.2)",
                  borderRadius: 2,
                  p: 1.5,
                  color: "white",
                  cursor: "default"
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <SupportAgent sx={{ color: "#60A5FA" }} />
                  <Typography variant="body2" fontWeight={600}>Support Portal</Typography>
                </Box>
                <Launch sx={{ fontSize: 16, color: "#60A5FA" }} />
              </Box>


            </Stack>
          </Grid>
        </Grid>


        <Divider sx={{ mb: 4, borderColor: "rgba(255,255,255,0.05)" }} />

        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            justifyContent: "space-between",
            alignItems: "center",
            gap: 3,
          }}
        >
          <Typography variant="body2" sx={{ color: "#64748B", fontSize: "0.85rem" }}>
            © 2026 ExiHire. All rights reserved.
          </Typography>
          
          <Stack direction="row" spacing={3} divider={<Divider orientation="vertical" flexItem sx={{ borderColor: "rgba(255,255,255,0.1)", height: 12, my: "auto" }} />}>
            <Typography variant="body2" sx={{ color: "#64748B", fontSize: "0.85rem", cursor: "default" }}>
              Privacy Policy
            </Typography>
            <Typography variant="body2" sx={{ color: "#64748B", fontSize: "0.85rem", cursor: "default" }}>
              Terms of Service
            </Typography>
            <Typography variant="body2" sx={{ color: "#64748B", fontSize: "0.85rem", cursor: "default" }}>
              Security
            </Typography>
          </Stack>

          <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>


            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box component="span" sx={{ px: 1, py: 0.2, bgcolor: "white", borderRadius: 0.5, color: "#1A1F71", fontWeight: 900, fontSize: "0.6rem" }}>VISA</Box>
              <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
                <Box component="span" sx={{ width: 8, height: 8, bgcolor: "#EB001B", borderRadius: "50%" }} />
                <Box component="span" sx={{ width: 8, height: 8, bgcolor: "#F79E1B", borderRadius: "50%" }} />
              </Box>
              <Box component="span" sx={{ px: 1, py: 0.2, bgcolor: "#5B21B6", borderRadius: 0.5, color: "white", fontWeight: 700, fontSize: "0.6rem" }}>UPI</Box>

            </Stack>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}

export default Footer;
